import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { startOfMonth, subMonths } from "date-fns";
import moment from "moment";

import { stateDate } from "../../../../utils/formatDate";

const initialState: any = {
    limit: JSON.parse(localStorage.getItem('pageLimit') as string) || 10,
    data: null,
    pages: 1,
    page: 1,
    applicationData: {
        firstName: '',
        lastName: '',
        gender: 'Male',
    },
    vacancyListInputValues: {
        cities: [],
        area: [],
        project: [],
        regions: [],
        status: ['INTERN_NOT_DIRECT', 'REPLACEMENT', 'TERRITORY_OPEN', 'VACATION', 'UNPROFITABLE', 'LINGERING_VACANT'],
        supervisors: [],
        type: null,
    },
    modifiedDate: {
        formattedDateFrom: moment(
            stateDate(startOfMonth(subMonths(new Date(), 1)))
        ).format("DD/MM/YYYY"),
        formattedDateTo: moment(stateDate(new Date())).format("DD/MM/YYYY"),
    },
    createDate: {
        formattedDateFrom: moment(
            stateDate(startOfMonth(subMonths(new Date(), 1)))
        ).format("DD/MM/YYYY"),
        formattedDateTo: moment(stateDate(new Date())).format("DD/MM/YYYY"),
    },
    vacancyListSettings: JSON.parse(
        localStorage.getItem('selectedVacancyListTabs') as string
    ) || [
            'vacancyId',
            'type',
            'regions',
            'areas',
            'cities',
            'subway',
            'addresses',
            'merchs',
            'vacancyName',
            'status',
            'projectName',
            'routeNo',
            'vsv',
            'sv',
            'schedule',
            'salary',
            'revenue',
            'razm',
            'createdDate',
            'modifiedDate',
        ],
    isPending: true,
    filters: {
        areas: [],
        projects: [],
        regions: [],
        statuses: [],
        supervisors: [],
        types: [],
        schedules: []
    },
};

export const vacancyListSlice = createSlice({
    name: 'vacancyList',
    initialState,
    reducers: {
        reducerGetVacancyList: (state: Draft<any>, action: PayloadAction<any>) => {
            if (action.payload?.data?.data?.length) {
                state.isPending = false
            }
            if (action.payload.data.count > 0 && action.payload.data.data) {
                state.data = action.payload.data.data.map((item: any) => {
                    const isChanged = Object.keys(item).some((key) => {
                        return item[key].changed === true;
                    });
                    const newArrValue = (value: any, valueExt: any) => {
                        const combinedAreas = [...(value || []), ...(valueExt || [])];
                        const uniqueAreas = combinedAreas.reduce((acc, el) => {
                            const existingIndex = el.employee ? acc.findIndex((a: any) => a.employee.id === el.employee.id ) : acc.findIndex((a: any) => a.id === el.id);
                            if (existingIndex === -1) {
                                acc.push(el);
                            }
                            return acc;
                        }, []);
                        return uniqueAreas;
                    }
                    const newStringValue = (item: any, name: string) => {
                        if (item[name].value && item[name].ext) {
                            const allValues = [...item[name].value, ...item[name].ext.split(',')];
                            const uniqueValues: any[] = Array.from(new Set(allValues));
                            return uniqueValues;
                        } else {
                            return (item[name].value || item[name].ext) ? (item[name].value || item[name].ext.split(",").map((val: any )=> val.trim())) : null;
                        }
                    }
                    
                    item.areas.valueAll = newArrValue(item.areas.value || [], item.areas.ext || []);
                    item.regions.valueAll = newArrValue(item.regions.value || [], item.regions.ext || []);
                    item.virtualSupervisors.valueAll = newArrValue(item.virtualSupervisors.value || [], item.virtualSupervisors.ext || []);
                    item.supervisors.valueAll = newArrValue(item.supervisors.value || [], item.supervisors.ext || []);
                    item.cities.valueAll = newStringValue(item, 'cities');
                    item.merchs.valueAll = newStringValue(item, 'merchs');
                    item.routes.valueAll = newStringValue(item, 'routes');

                    if (item.addresses.value && item.addresses.ext) {
                        const arr: any = [];
                        item?.addresses?.value?.map((e: any) => {
                            if (!item.addresses?.ext?.includes(e)) {
                                arr.push(e)
                            }
                            return e;
                        })
                        const uniqueCities = arr.concat(item.addresses.ext);
                        item.addresses.valueAll = uniqueCities;
                    } else {
                        item.addresses.valueAll = (item.addresses.value || item.addresses.ext) ? item.addresses.value || [item.addresses.ext] : null;
                    }

                    if (item.schedule.value && item.schedule.ext) {
                        if (Object.keys(item.schedule.value)[0] === Object.keys(item.schedule.ext)[0]) {
                            item.schedule.valueAll = [item.schedule.value];
                        } else {
                            item.schedule.valueAll = [item.schedule.value, item.schedule.ext];
                        }
                    } else {
                        item.schedule.valueAll = item.schedule.value || item.schedule.ext ? [item.schedule.value || item.schedule.ext] : null;
                    }

                    return {
                        ...item,
                        isChanged: isChanged
                    };
                });
            } else {
                state.data = [];
            }

            state.count = action.payload.data.count;
            state.checkboxes = action.payload.data?.data?.reduce(
                (acc: any, vacancy: any) => {
                    const vacancyId = vacancy.vacancy.value.id;
                    acc[vacancyId] = false;
                    return acc;
                },
                {}
            );
        },
        reducerGetProject: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.vacancyListInputValues.project = action.payload;
        },
        reducerSetVacancyPending: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.isPending = action.payload;
        },
        reducerSelectedVacanciesId: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.selectedVacanciesId = action.payload;
        },
        reducerSelectedVacancies: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.selectedVacancy = action.payload;
        },
        reducerVacancyStatus: (state: Draft<any>, action: PayloadAction<any>) => {
            state.clientsStatus = action.payload;
        },
        reducerVacancyStatusMessage: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.clientsStatusMessage = action.payload;
        },
        reducerApplicationChange: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.applicationData = action.payload;
        },
        reducerVacancyListSettings: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.vacancyListSettings = action.payload;
        },
        reducerVacancyListInputValues: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.vacancyListInputValues = action.payload;
            if (action.payload.modifiedDate) {
                state.modifiedDate.formattedDateFrom = moment(action.payload.modifiedDate?.from).format(
                    "DD/MM/YYYY"
                );
                state.modifiedDate.formattedDateTo = moment(action.payload.modifiedDate?.to).format(
                    "DD/MM/YYYY"
                );
            }
            if (action.payload.createDate) {
                state.createDate.formattedDateFrom = moment(action.payload.createDate?.from).format(
                    "DD/MM/YYYY"
                );
                state.createDate.formattedDateTo = moment(action.payload.createDate?.to).format(
                    "DD/MM/YYYY"
                );
            }
        },
        reducerVacancyFiltersList: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.filters.areas = action.payload.data.areas?.map(
                (option: any) => ({
                    value: option,
                    label: option.name,
                })
            );

            state.filters.regions = action.payload.data.reigons?.map(
                (option: any) => ({
                    value: option,
                    label: option.name

                })
            );

            state.filters.schedules = action.payload.data.schedules?.map(
                (option: any) => ({
                    value: Object.keys(option)[0],
                    label: Object.values(option)[0],
                })
            );

            state.filters.statuses = action.payload.data.statuses?.map(
                (option: any) => ({
                    value: Object.keys(option)[0],
                    label: Object.values(option)[0],
                })
            );

            state.filters.types = action.payload.data.types?.map(
                (option: any) => ({
                    value: Object.keys(option)[0],
                    label: Object.values(option)[0],
                })
            );

            state.filters.projects = action.payload.data.projects?.map(
                (option: any) => ({
                    value: option,
                    label: option.name,
                })
            );

            state.filters.supervisors = action.payload.data.supervisors?.map(
                (option: any) => ({
                    value: option,
                    label: option.name,
                })
            );

            state.filters.virtualSupervisors = action.payload.data.virtualSupervisors?.map(
                (option: any) => ({
                    value: option,
                    label: option.name,
                })
            );
        },
        reducerVacancyMultiselect: (
            state: Draft<any>,
            action: PayloadAction<any>
        ) => {
            state.vacancyListInputValues = { ...state.vacancyListInputValues, ...action.payload };
        },
        reducerVacancyClearInputValues: (
            state: Draft<any>,
        ) => {
            state.vacancyListInputValues = {
                cities: [],
                area: [],
                project: [],
                regions: [],
                status: ['INTERN_NOT_DIRECT', 'REPLACEMENT', 'TERRITORY_OPEN', 'VACATION', 'UNPROFITABLE', 'LINGERING_VACANT'],
                supervisors: [],
                type: null,
            };
        },
    },
});

export const {
    reducerGetVacancyList,
    reducerSelectedVacancies,
    reducerSelectedVacanciesId,
    reducerVacancyStatus,
    reducerVacancyStatusMessage,
    reducerApplicationChange,
    reducerVacancyListSettings,
    reducerVacancyListInputValues,
    reducerSetVacancyPending,
    reducerVacancyFiltersList,
    reducerVacancyMultiselect,
    reducerGetProject,
    reducerVacancyClearInputValues,
} = vacancyListSlice.actions;

export default vacancyListSlice.reducer;
